import { createRouter, createWebHistory } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import type { UserRole } from '@/types'

// Lazy load components
const Home = () => import('@/views/Home.vue')
const Login = () => import('@/views/auth/Login.vue')
const Register = () => import('@/views/auth/Register.vue')
const ForgotPassword = () => import('@/views/auth/ForgotPassword.vue')
const ResetPassword = () => import('@/views/auth/ResetPassword.vue')
const Dashboard = () => import('@/views/Dashboard.vue')
const Profile = () => import('@/views/Profile.vue')
const Search = () => import('@/views/Search.vue')
const Messages = () => import('@/views/Messages.vue')
const PlayerListings = () => import('@/views/listings/PlayerListings.vue')
const TeamListings = () => import('@/views/listings/TeamListings.vue')
const CreatePlayerListing = () => import('@/views/listings/CreatePlayerListing.vue')
const CreateTeamListing = () => import('@/views/listings/CreateTeamListing.vue')
const ListingDetail = () => import('@/views/listings/ListingDetail.vue')
const AdminDashboard = () => import('@/views/admin/AdminDashboard.vue')
const AdminUsers = () => import('@/views/admin/AdminUsers.vue')
const AdminUserDetail = () => import('@/views/admin/AdminUserDetail.vue')
const AdminReports = () => import('@/views/admin/AdminReports.vue')
const AdminManagePlayers = () => import('@/views/admin/AdminManagePlayers.vue')
const AdminCoachAuthorisations = () => import('@/views/admin/AdminCoachAuthorisations.vue')
const AdminCountyFas = () => import('@/views/admin/AdminCountyFas.vue')
const AdminClubs = () => import('@/views/admin/AdminClubs.vue')
const AdminTeams = () => import('@/views/admin/AdminTeams.vue')
const PrivacyPolicy = () => import('@/views/legal/PrivacyPolicy.vue')
const TermsOfService = () => import('@/views/legal/TermsOfService.vue')
const CookiePolicy = () => import('@/views/legal/CookiePolicy.vue')
const ContactUs = () => import('@/views/legal/ContactUs.vue')
const AboutUs = () => import('@/views/AboutUs.vue')
const HelpCenter = () => import('@/views/support/HelpCenter.vue')
const HoldingPage = () => import('@/views/HoldingPage.vue')
const NotFound = () => import('@/views/NotFound.vue')
const Shortlist = () => import('@/views/Shortlist.vue')

const routes = [
  {
    path: '/',
    name: 'Home',
    component: Home,
    meta: { requiresAuth: false }
  },
  {
    path: '/shortlist',
    name: 'Shortlist',
    component: Shortlist,
    meta: {
      requiresAuth: true,
      allowedRoles: ['coach', 'club_admin', 'system_admin'] as UserRole[]
    }
  },
  {
    path: '/holding-page',
    name: 'HoldingPage',
    component: HoldingPage,
    meta: { requiresAuth: false }
  },
  {
    path: '/login',
    name: 'Login',
    component: Login,
    meta: { requiresAuth: false, requiresGuest: true }
  },
  {
    path: '/register',
    name: 'Register',
    component: Register,
    meta: { requiresAuth: false, requiresGuest: true }
  },
  {
    path: '/forgot-password',
    name: 'ForgotPassword',
    component: ForgotPassword,
    meta: { requiresAuth: false, requiresGuest: true }
  },
  {
    path: '/reset-password',
    name: 'ResetPassword',
    component: ResetPassword,
    meta: { requiresAuth: false }
  },
  {
    path: '/dashboard',
    name: 'Dashboard',
    component: Dashboard,
    meta: { requiresAuth: true }
  },
  {
    path: '/profile',
    name: 'Profile',
    component: Profile,
    meta: { requiresAuth: true }
  },
  {
    path: '/search',
    name: 'Search',
    component: Search,
    meta: { requiresAuth: false }
  },
  {
    path: '/messages',
    name: 'Messages',
    component: Messages,
    meta: { requiresAuth: true }
  },
  {
    path: '/players',
    name: 'PlayerListings',
    component: PlayerListings,
    meta: { requiresAuth: false }
  },
  {
    path: '/teams',
    name: 'TeamListings',
    component: TeamListings,
    meta: { requiresAuth: false }
  },
  {
    path: '/create-player-listing',
    name: 'CreatePlayerListing',
    component: CreatePlayerListing,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['player', 'parent_guardian', 'system_admin'] as UserRole[]
    }
  },
  {
    path: '/create-player-listing/:id',
    name: 'EditPlayerListing',
    component: CreatePlayerListing,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['player', 'parent_guardian', 'system_admin'] as UserRole[]
    }
  },
  {
    path: '/create-team-listing',
    name: 'CreateTeamListing',
    component: CreateTeamListing,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['coach', 'club_admin', 'system_admin'] as UserRole[]
    }
  },
  {
    path: '/create-team-listing/:id',
    name: 'EditTeamListingLegacy',
    component: CreateTeamListing,
    meta: {
      requiresAuth: true,
      allowedRoles: ['coach', 'club_admin', 'system_admin'] as UserRole[]
    }
  },
  {
    path: '/edit-team-listing/:id',
    name: 'EditTeamListing',
    component: CreateTeamListing,
    meta: {
      requiresAuth: true,
      allowedRoles: ['coach', 'club_admin', 'system_admin'] as UserRole[]
    }
  },
  {
    path: '/listing/:type/:id',
    name: 'ListingDetail',
    component: ListingDetail,
    meta: { requiresAuth: false }
  },
  {
    path: '/admin',
    name: 'AdminDashboard',
    component: AdminDashboard,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/admin/users',
    name: 'AdminUsers',
    component: AdminUsers,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/admin/users/:id',
    name: 'AdminUserDetail',
    component: AdminUserDetail,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/admin/reports',
    name: 'AdminReports',
    component: AdminReports,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/admin/manage-players',
    name: 'AdminManagePlayers',
    component: AdminManagePlayers,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/admin/coach-authorisations',
    name: 'AdminCoachAuthorisations',
    component: AdminCoachAuthorisations,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/admin/county-fas',
    name: 'AdminCountyFas',
    component: AdminCountyFas,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/admin/clubs',
    name: 'AdminClubs',
    component: AdminClubs,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/admin/teams',
    name: 'AdminTeams',
    component: AdminTeams,
    meta: { 
      requiresAuth: true,
      allowedRoles: ['system_admin'] as UserRole[]
    }
  },
  {
    path: '/privacy-policy',
    name: 'PrivacyPolicy',
    component: PrivacyPolicy,
    meta: { requiresAuth: false }
  },
  {
    path: '/terms-of-service',
    name: 'TermsOfService',
    component: TermsOfService,
    meta: { requiresAuth: false }
  },
  {
    path: '/cookie-policy',
    name: 'CookiePolicy',
    component: CookiePolicy,
    meta: { requiresAuth: false }
  },
  {
    path: '/contact',
    name: 'ContactUs',
    component: ContactUs,
    meta: { requiresAuth: false }
  },
  {
    path: '/about-us',
    name: 'AboutUs',
    component: AboutUs,
    meta: { requiresAuth: false }
  },
  {
    path: '/help-center',
    name: 'HelpCenter',
    component: HelpCenter,
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'NotFound',
    component: NotFound,
    meta: { requiresAuth: false }
  }
]

const router = createRouter({
  history: createWebHistory(),
  routes,
  scrollBehavior(to, _, savedPosition) {
    if (savedPosition) {
      return savedPosition
    } else if (to.hash) {
      return { el: to.hash }
    } else {
      return { top: 0 }
    }
  }
})

// Navigation guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  
  // Wait for auth to initialize
  if (!authStore.initialized) {
    await authStore.initialize()
  }

  // Debug logging for navigation
  console.log('🧭 Router Guard - Navigation attempt:', {
    toPath: to.path,
    fromPath: from.path,
    isAuthenticated: authStore.isAuthenticated,
    userRole: authStore.userRole,
    isSystemAdmin: authStore.isSystemAdmin,
    allowedRoles: to.meta.allowedRoles,
    requiresAuth: to.meta.requiresAuth,
    requiresGuest: to.meta.requiresGuest
  })

  const requiresAuth = to.meta.requiresAuth
  const requiresGuest = to.meta.requiresGuest
  const allowedRoles = to.meta.allowedRoles as UserRole[]

  // Check if route requires authentication
  if (requiresAuth && !authStore.isAuthenticated) {
    // Don't try to restore if we have neither session nor user (likely logged out)
    // Only try to restore if we have a session but temporarily lost user state
    if (authStore.session && !authStore.user && authStore.session.expires_at) {
      // Check if session is still valid
      const sessionExpiry = new Date(authStore.session.expires_at * 1000)
      const isSessionExpired = sessionExpiry <= new Date()
      
      if (!isSessionExpired) {
        console.log('🔄 Router Guard - Session exists but no user, attempting restore...')
        try {
          await authStore.restoreUserSession()
          // If restoration was successful, continue with navigation
          if (authStore.isAuthenticated) {
            console.log('✅ Router Guard - User session restored, continuing navigation')
            next()
            return
          }
        } catch (error) {
          console.error('❌ Router Guard - Failed to restore user session:', error)
        }
      } else {
        console.log('⏱️ Router Guard - Session expired, not attempting restore')
      }
    }
    
    console.log('❌ Router Guard - Redirecting to login (not authenticated)')
    next({ name: 'Login', query: { redirect: to.fullPath } })
    return
  }

  // Check if route requires guest (not authenticated)
  if (requiresGuest && authStore.isAuthenticated) {
    console.log('❌ Router Guard - Redirecting to dashboard (already authenticated)')
    next({ name: 'Dashboard' })
    return
  }

  // Check role-based access
  if (allowedRoles && authStore.isAuthenticated) {
    if (!authStore.hasAnyRole(allowedRoles)) {
      console.log('❌ Router Guard - Redirecting to dashboard (insufficient role)', {
        userRole: authStore.userRole,
        requiredRoles: allowedRoles,
        hasAnyRole: authStore.hasAnyRole(allowedRoles)
      })
      next({ name: 'Dashboard' })
      return
    }
  }

  console.log('✅ Router Guard - Access granted')
  next()
})

export default router